/**
 * Shared UploadModeSelector component for upload forms
 * Handles upload mode and source language selection for both photo and CSV uploads
 */

import React from 'react';
import { Select, Stack } from '@mantine/core';

export interface UploadModeSelectorProps {
  uploadMode: string;
  onUploadModeChange: (value: string) => void;
  sourceLanguage: string;
  onSourceLanguageChange: (value: string) => void;
  loading: boolean;
}

/**
 * UploadModeSelector component for handling upload mode and source language selection
 * Provides consistent upload configuration UI across upload pages
 */
export const UploadModeSelector: React.FC<UploadModeSelectorProps> = ({
  uploadMode,
  onUploadModeChange,
  sourceLanguage,
  onSourceLanguageChange,
  loading
}) => {
  const uploadModeOptions = [
    { value: 'v2', label: 'v2 Mode (Translation + background + ingredient extraction)' },
    { value: 'no-translation', label: 'No Translation Mode (Skip translation & localization)' }
  ];

  const sourceLanguageOptions = [
    { value: 'jp', label: 'Japanese' },
    { value: 'ko', label: 'Korean' },
    { value: 'zh', label: 'Chinese' },
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Spanish' },
    { value: 'fr', label: 'French' },
    { value: 'de', label: 'German' },
    { value: 'it', label: 'Italian' },
    { value: 'pt', label: 'Portuguese' },
    { value: 'ru', label: 'Russian' }
  ];

  return (
    <Stack gap="md">
      <Select
        label="Upload Mode"
        placeholder="Choose upload mode"
        data={uploadModeOptions}
        value={uploadMode}
        onChange={(value) => onUploadModeChange(value || 'v2')}
        disabled={loading}
        description="v2 mode generates English translations, background context, and extracts ingredients. No translation mode skips these steps for faster processing."
      />
      
      <Select
        label="Source Language"
        placeholder="Choose source language"
        data={sourceLanguageOptions}
        value={sourceLanguage}
        onChange={(value) => onSourceLanguageChange(value || 'jp')}
        disabled={loading}
        description="The original language of the menu items. This will be stored as the source language in the new translation structure."
      />
    </Stack>
  );
};

export default UploadModeSelector;
